/**
 * Shared helpers for BETA handlers — pagination, filtering, totals math.
 */
import { newId, nowIso, todayYmd, dateOffsetYmd } from "../uuid.js";

export { newId, nowIso, todayYmd, dateOffsetYmd };

/** Standard pagination wrapper. */
export function paginate(rows, params, extra = {}) {
  const page = Number(params.page) || 1;
  const limit = Number(params.limit) || 20;
  const start = (page - 1) * limit;
  return {
    data: rows.slice(start, start + limit),
    total: rows.length,
    page,
    limit,
    ...extra,
  };
}

/** Case-insensitive partial-match across a set of fields. */
export function matchSearch(row, q, fields) {
  if (!q) return true;
  const needle = String(q).toLowerCase();
  return fields.some((f) => String(row?.[f] ?? "").toLowerCase().includes(needle));
}

/** Round to 2 decimals — mirrors backend money discipline. */
export function money(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Recompute vente line + header totals (used by ventes + instances). */
export function recomputeVenteTotals(vente) {
  let ttc = 0;
  let pec = 0;
  for (const d of vente.details ?? []) {
    const lineTtc = money(d.quantite * d.prix_unitaire * (1 - (d.remise_pct || 0) / 100));
    const linePec = vente.type_vente === "CHIFA" ? money(lineTtc * 0.8) : 0;
    d.montant_ligne_ttc = lineTtc;
    d.montant_pec_chifa = linePec;
    d.montant_patient = money(lineTtc - linePec);
    ttc += lineTtc;
    pec += linePec;
  }
  ttc = money(ttc);
  pec = money(pec);
  vente.montant_total_ttc = ttc;
  vente.montant_total_ht = money(ttc / 1.19);
  vente.montant_total_tva = money(ttc - vente.montant_total_ht);
  vente.montant_pec_chifa = pec;
  vente.montant_a_payer = money(ttc - pec);
  vente.updated_at = nowIso();
}

/** Recompute achat totals. */
export function recomputeAchatTotals(achat) {
  let ht = 0;
  let ttc = 0;
  for (const d of achat.details ?? []) {
    const lineHt = money(d.quantite * d.prix_achat_ht);
    const lineTtc = money(lineHt * (1 + (d.tva_pct || 0) / 100));
    d.montant_ligne_ht = lineHt;
    d.montant_ligne_ttc = lineTtc;
    ht += lineHt;
    ttc += lineTtc;
  }
  achat.montant_ht = money(ht);
  achat.montant_ttc = money(ttc);
  achat.montant_tva = money(ttc - ht);
  achat.reste_a_payer = money(ttc - (achat.montant_paye || 0));
  achat.updated_at = nowIso();
}

/** FEFO-deduct `qty` from the lots of `produitId`. Returns the affected lot ids. */
export function fefoDeduct(store, produitId, qty) {
  const lots = store.data.stockLots
    .filter((l) => l.produit_id === produitId && l.quantite_actuelle > 0)
    .sort((a, b) => String(a.date_peremption).localeCompare(String(b.date_peremption)));
  const affected = [];
  let remaining = qty;
  for (const lot of lots) {
    if (remaining <= 0) break;
    const take = Math.min(lot.quantite_actuelle, remaining);
    lot.quantite_actuelle -= take;
    remaining -= take;
    affected.push({ lotId: lot.id, taken: take });
  }
  const produit = store.data.produits.find((p) => p.id === produitId);
  if (produit) produit.stock_actuel = Math.max(0, (produit.stock_actuel || 0) - qty);
  return affected;
}

/** Restore qty back to the first lot of `produitId` (cancel-vente flow). */
export function restoreStock(store, produitId, qty) {
  const lots = store.data.stockLots.filter((l) => l.produit_id === produitId);
  if (lots.length === 0) return;
  lots[0].quantite_actuelle += qty;
  const produit = store.data.produits.find((p) => p.id === produitId);
  if (produit) produit.stock_actuel = (produit.stock_actuel || 0) + qty;
}
